interface WebhookData {
  quizName: string;
  userInfo: any;
  responses: any[];
  timestamp: string;
}

interface PendingSubmission {
  id: string;
  data: WebhookData;
  attempts: number;
  lastAttempt: string;
}

const WEBHOOK_URL = "https://intherax.io/webhook/8d536339-e3b2-4e56-81fd-0329c646e9fd";
const PENDING_SUBMISSIONS_KEY = "pending_webhook_submissions";
const MAX_RETRY_ATTEMPTS = 24; // 24 hours worth of retries (1 per hour)

class WebhookService {
  private retryInterval: NodeJS.Timeout | null = null;

  private normalizeQuizData(data: WebhookData): WebhookData {
    if (data.quizName === "AI & Business Transformation Readiness Assessment") {
      return {
        ...data,
        quizName: "Diagnostics Quiz",
      };
    }

    return data;
  }

  constructor() {
    this.initializeRetryMechanism();
  }

  /**
   * Send quiz data to webhook
   */
  async sendQuizData(data: WebhookData): Promise<boolean> {
    const normalizedData = this.normalizeQuizData(data);

    try {
      console.log(
        `📤 Sending ${normalizedData.quizName} data to webhook...`,
        normalizedData
      );

      const response = await fetch(WEBHOOK_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        keepalive: true,
        body: JSON.stringify(normalizedData),
      });

      if (response.ok) {
        console.log(
          `✅ ${normalizedData.quizName} data sent successfully to webhook`
        );
        return true;
      } else {
        console.error(
          `❌ Webhook returned error status for ${normalizedData.quizName}:`,
          response.status
        );
        return false;
      }
    } catch (error) {
      console.error(
        `❌ Failed to send ${normalizedData.quizName} data to webhook:`,
        error
      );

      // Fallback for endpoints that reject CORS preflight for JSON.
      try {
        await fetch(WEBHOOK_URL, {
          method: "POST",
          mode: "no-cors",
          headers: {
            "Content-Type": "text/plain;charset=UTF-8",
          },
          keepalive: true,
          body: JSON.stringify(normalizedData),
        });

        console.log(
          `✅ ${normalizedData.quizName} data sent using no-cors fallback`
        );
        return true;
      } catch (fallbackError) {
        console.error(
          `❌ Fallback send failed for ${normalizedData.quizName}:`,
          fallbackError
        );
        return false;
      }
    }
  }

  /**
   * Save submission locally for retry
   */
  private saveForRetry(data: WebhookData): void {
    const pendingSubmissions = this.getPendingSubmissions();
    const newSubmission: PendingSubmission = {
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      data,
      attempts: 0,
      lastAttempt: new Date().toISOString(),
    };

    pendingSubmissions.push(newSubmission);
    localStorage.setItem(
      PENDING_SUBMISSIONS_KEY,
      JSON.stringify(pendingSubmissions)
    );
    console.log("💾 Submission saved locally for retry");
  }

  /**
   * Get all pending submissions from localStorage
   */
  private getPendingSubmissions(): PendingSubmission[] {
    try {
      const stored = localStorage.getItem(PENDING_SUBMISSIONS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error("Error reading pending submissions:", error);
      return [];
    }
  }

  /**
   * Remove a submission from pending list
   */
  private removeSubmission(id: string): void {
    const pendingSubmissions = this.getPendingSubmissions();
    const filtered = pendingSubmissions.filter((sub) => sub.id !== id);
    localStorage.setItem(PENDING_SUBMISSIONS_KEY, JSON.stringify(filtered));
  }

  /**
   * Submit quiz data with automatic retry mechanism
   */
  async submitQuizData(data: WebhookData): Promise<boolean> {
    const success = await this.sendQuizData(data);

    if (!success) {
      // Save for retry if initial send fails
      this.saveForRetry(data);
    }

    return success;
  }

  /**
   * Retry all pending submissions
   */
  private async retryPendingSubmissions(): Promise<void> {
    const pendingSubmissions = this.getPendingSubmissions();

    if (pendingSubmissions.length === 0) {
      return;
    }

    console.log(
      `🔄 Retrying ${pendingSubmissions.length} pending submission(s)...`
    );

    for (const submission of pendingSubmissions) {
      // Skip if max attempts reached
      if (submission.attempts >= MAX_RETRY_ATTEMPTS) {
        console.log(
          `⚠️ Max retry attempts reached for submission ${submission.id}, removing...`
        );
        this.removeSubmission(submission.id);
        continue;
      }

      const success = await this.sendQuizData(submission.data);

      if (success) {
        console.log(`✅ Successfully sent pending submission ${submission.id}`);
        this.removeSubmission(submission.id);
      } else {
        // Update attempt count and timestamp
        submission.attempts += 1;
        submission.lastAttempt = new Date().toISOString();
        const allSubmissions = this.getPendingSubmissions();
        const index = allSubmissions.findIndex((s) => s.id === submission.id);
        if (index !== -1) {
          allSubmissions[index] = submission;
          localStorage.setItem(
            PENDING_SUBMISSIONS_KEY,
            JSON.stringify(allSubmissions)
          );
        }
      }
    }
  }

  /**
   * Initialize automatic retry mechanism (runs every hour)
   */
  private initializeRetryMechanism(): void {
    // Clear any existing interval
    if (this.retryInterval) {
      clearInterval(this.retryInterval);
    }

    // Retry immediately on initialization
    this.retryPendingSubmissions();

    // Set up hourly retry
    this.retryInterval = setInterval(() => {
      this.retryPendingSubmissions();
    }, 60 * 60 * 1000); // 1 hour in milliseconds
  }

  /**
   * Clean up when service is destroyed
   */
  destroy(): void {
    if (this.retryInterval) {
      clearInterval(this.retryInterval);
      this.retryInterval = null;
    }
  }

  /**
   * Get count of pending submissions (for debugging/monitoring)
   */
  getPendingCount(): number {
    return this.getPendingSubmissions().length;
  }
}

// Export singleton instance
export const webhookService = new WebhookService();

// Export types for use in components
export type { WebhookData };
